(() => {
  'use strict';

  const GLOBAL_FLAG = '__entrevoltasColorContextLoaded';
  const ROOT_SELECTOR = 'product-info[id^="MainProduct-"]';
  const COLOR_NEUTRAL_LABEL = 'escolha uma cor';
  const COLOR_REQUIRED_MESSAGE = 'Escolha uma cor';
  const MODEL_NEUTRAL_LABEL = 'escolha uma modelagem';
  const MODEL_REQUIRED_MESSAGE = 'Escolha uma modelagem';
  const SIZE_NEUTRAL_LABEL = 'escolha um tamanho';
  const SIZE_REQUIRED_MESSAGE = 'Escolha um tamanho';
  const SIZE_UNAVAILABLE_MESSAGE = 'Este tamanho está indisponível no momento. Escolha outro.';
  const states = new WeakMap();

  if (window[GLOBAL_FLAG]) return;
  window[GLOBAL_FLAG] = true;

  const normalize = (value) =>
    String(value || '')
      .trim()
      .replace(/\s+/g, ' ')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('pt-BR');

  const isColorName = (value) => {
    const key = normalize(value).replace(/:$/, '');
    return key === 'cor' || key === 'color' || key.startsWith('cor ') || key.startsWith('color ');
  };

  const optionNameForField = (field) => {
    const radio = field.querySelector('input[type="radio"][name]');
    if (radio) return String(radio.name || '').trim().replace(/-\d+$/, '');

    const select = field.querySelector('select[name]');
    if (select) {
      const match = String(select.name || '').trim().match(/^options\[(.*)\]$/);
      return match ? match[1] : select.getAttribute('aria-label') || '';
    }

    const label = field.querySelector('.form__label');
    return label ? String(label.textContent || '').split(':')[0] : '';
  };

  const optionValuesForField = (field) => {
    const radios = Array.from(field.querySelectorAll('input[type="radio"]'));
    if (radios.length) return radios.map((input) => normalize(input.value));

    const select = field.querySelector('select');
    if (select) {
      return Array.from(select.options).map((option) => normalize(option.value || option.textContent));
    }

    return [];
  };

  const isModelingField = (field) => {
    const values = new Set(optionValuesForField(field));
    return values.has('feminino') && values.has('masculino');
  };

  const variantFields = (productInfo) =>
    Array.from(productInfo.querySelector('variant-selects')?.querySelectorAll('.product-form__input') || []);

  const findColorField = (productInfo) => {
    const marked = productInfo.querySelector('variant-selects')?.querySelector('[data-ev-color-field="true"]');
    if (marked) return marked;

    const field = variantFields(productInfo).find(
      (candidate) => isColorName(optionNameForField(candidate))
    ) || null;
    if (field) field.dataset.evColorField = 'true';
    return field;
  };

  const findModelingField = (productInfo) => {
    const marked = productInfo.querySelector('variant-selects')?.querySelector('[data-ev-model-field="true"]');
    if (marked) return marked;

    const field = variantFields(productInfo).find(isModelingField) || null;
    if (field) field.dataset.evModelField = 'true';
    return field;
  };

  const findSizeField = (productInfo) => {
    const marked = productInfo.querySelector('variant-selects')?.querySelector('[data-ev-size-field="true"]');
    if (marked) return marked;
    const field = variantFields(productInfo).find((candidate) => {
      const key = normalize(optionNameForField(candidate)).replace(/:$/, '');
      return key === 'tamanho' || key === 'size';
    }) || null;
    if (field) field.dataset.evSizeField = 'true';
    return field;
  };

  const selectedValueNode = (field) =>
    field?.querySelector('.form__label [data-selected-value]') ||
    field?.querySelector('[data-selected-value]') ||
    null;

  const currentFieldValue = (field) => {
    if (!field) return '';
    const checked = field.querySelector('input[type="radio"]:checked');
    if (checked) return checked.value;
    const select = field.querySelector('select');
    return select?.selectedOptions?.[0]?.value || field.dataset.evFallbackValue || '';
  };

  const readSelectedVariant = (productInfo) => {
    const raw = productInfo.querySelector(
      'variant-selects [data-selected-variant]'
    )?.textContent;
    if (!raw?.trim()) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };

  const variantMediaId = (productInfo, variant) => {
    const mediaId = variant?.featured_media?.id;
    return mediaId ? String(productInfo.dataset.section || '') + '-' + String(mediaId) : null;
  };

  const updateShareUrl = (productInfo, url) => {
    productInfo.querySelector('share-button')?.updateUrl?.(url.toString());
  };

  const removeVariantParam = (productInfo) => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('variant')) return;
    url.searchParams.delete('variant');
    window.history.replaceState(
      {},
      '',
      url.pathname + url.search + url.hash
    );
    updateShareUrl(productInfo, url);
  };

  const restoreVariantParam = (productInfo, variantId) => {
    if (!variantId || productInfo.dataset.updateUrl === 'false') return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variantId));
    window.history.replaceState(
      {},
      '',
      url.pathname + url.search + url.hash
    );
    updateShareUrl(productInfo, url);
  };

  const ensureMessage = (field, className, text) => {
    if (!field) return null;
    let message = field.querySelector('.' + className);
    if (message) return message;

    message = document.createElement('p');
    message.className = className;
    message.setAttribute('role', 'status');
    message.setAttribute('aria-live', 'polite');
    message.hidden = true;
    message.textContent = text;
    field.appendChild(message);
    return message;
  };

  const setMessage = (field, className, text, visible) => {
    const message = ensureMessage(field, className, text);
    if (message) {
      if (message.textContent !== text) message.textContent = text;
      message.hidden = !visible;
    }
  };

  const setSelectedText = (field, value) => {
    const node = selectedValueNode(field);
    if (node) node.textContent = value;
  };

  const hasNeutralRequiredSelection = (state) =>
    Boolean(state && (
      state.mode === 'neutral' ||
      state.modelMode === 'neutral' ||
      state.sizeMode === 'neutral'
    ));

  const selectionComplete = (state) => Boolean(state && !hasNeutralRequiredSelection(state));

  const preserveUnconfirmedField = (field, neutral) => {
    if (!field) return;
    const checked = field.querySelector('input[type="radio"]:checked');
    const select = field.querySelector('select');
    const selected = checked || select?.selectedOptions?.[0];
    if (neutral) {
      if (selected) {
        field.dataset.evFallbackValue = selected.value;
        field.dataset.evFallbackOptionValueId = selected.dataset.optionValueId || '';
      }
      field.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.checked = false;
        input.removeAttribute('checked');
      });
      if (select) select.selectedIndex = -1;
      field.setAttribute('aria-invalid', 'true');
    } else {
      delete field.dataset.evFallbackValue;
      delete field.dataset.evFallbackOptionValueId;
      field.removeAttribute('aria-invalid');
    }
  };

  const canPurchase = (productInfo) => {
    const state = states.get(productInfo);
    if (!state) return !productInfo?.matches?.(ROOT_SELECTOR);
    if (!selectionComplete(state) || state.variantPending) return false;
    const variant = readSelectedVariant(productInfo);
    return Boolean(variant?.id && variant.available &&
      variant.options?.every((value, index) => value === currentFieldValue(variantFields(productInfo)[index])));
  };

  const syncPurchaseState = (productInfo) => {
    const state = states.get(productInfo);
    if (!state) return;
    const blocked = !canPurchase(productInfo);
    productInfo.dataset.evPurchaseBlocked = String(blocked);
    productInfo.querySelectorAll('form[data-type="add-to-cart-form"] input[name="id"]').forEach((input) => {
      input.disabled = blocked;
      input.dataset.evSelectionApproved = String(!blocked);
    });
    productInfo.querySelectorAll('form[data-type="add-to-cart-form"] button[type="submit"], .sticky-atc-trigger, .lumin-custom-buy-now, .shopify-payment-button button').forEach((button) => {
      button.disabled = blocked;
      button.setAttribute('aria-disabled', String(blocked));
      if (!state.variantPending && button.matches('button[type="submit"]')) {
        const variant = readSelectedVariant(productInfo);
        const text = button.querySelector('span');
        if (text && window.variantStrings) text.textContent = variant?.available
          ? window.variantStrings.addToCart : variant?.id ? window.variantStrings.soldOut : window.variantStrings.unavailable;
      }
    });
    productInfo.querySelectorAll('.shopify-payment-button').forEach((node) => {
      node.inert = blocked;
      node.setAttribute('aria-disabled', String(blocked));
    });
  };

  const applyVisualState = (productInfo) => {
    const state = states.get(productInfo);
    if (!state) return;
    productInfo.setAttribute('aria-busy', String(state.variantPending));

    const colorField = findColorField(productInfo);
    preserveUnconfirmedField(colorField, state.mode === 'neutral');
    if (colorField) {
      productInfo.dataset.evColorState = state.mode;
      if (state.mode === 'neutral') {
        setSelectedText(colorField, COLOR_NEUTRAL_LABEL);
      } else {
        const value = currentFieldValue(colorField);
        if (value) setSelectedText(colorField, value);
      }
      setMessage(
        colorField,
        'ev-color-required-message',
        COLOR_REQUIRED_MESSAGE,
        state.showColorMessage === true
      );
    }

    const modelField = findModelingField(productInfo);
    preserveUnconfirmedField(modelField, state.modelMode === 'neutral');
    if (modelField) {
      productInfo.dataset.evModelState = state.modelMode;
      if (state.modelMode === 'neutral') {
        modelField.dataset.evModelNeutral = 'true';
        setSelectedText(modelField, MODEL_NEUTRAL_LABEL);
      } else {
        delete modelField.dataset.evModelNeutral;
        const value = currentFieldValue(modelField);
        if (value) setSelectedText(modelField, value);
      }
      setMessage(
        modelField,
        'ev-model-required-message',
        MODEL_REQUIRED_MESSAGE,
        state.showModelMessage === true
      );
    } else {
      productInfo.dataset.evModelState = 'not-applicable';
    }

    const sizeField = findSizeField(productInfo);
    preserveUnconfirmedField(sizeField, state.sizeMode === 'neutral');
    if (sizeField) {
      productInfo.dataset.evSizeState = state.sizeMode;
      if (state.sizeMode === 'neutral') {
        setSelectedText(sizeField, SIZE_NEUTRAL_LABEL);
        sizeField.setAttribute('aria-invalid', 'true');
      } else {
        const size = currentFieldValue(sizeField);
        if (size) setSelectedText(sizeField, size);
        sizeField.removeAttribute('aria-invalid');
      }
      setMessage(
        sizeField,
        'ev-size-required-message',
        state.sizeUnavailable ? SIZE_UNAVAILABLE_MESSAGE : SIZE_REQUIRED_MESSAGE,
        state.showSizeMessage === true
      );
    } else {
      productInfo.dataset.evSizeState = 'not-applicable';
    }
    syncPurchaseState(productInfo);
  };

  const reapplySoon = (productInfo) => {
    applyVisualState(productInfo);
    queueMicrotask(() => { if (productInfo.isConnected) applyVisualState(productInfo); });
  };

  const clearPending = (state) => {
    state.variantPending = false;
    if (state.pendingTimer) {
      window.clearTimeout(state.pendingTimer);
      state.pendingTimer = null;
    }
  };

  const markPending = (state) => {
    state.variantPending = true;
    syncPurchaseState(Array.from(document.querySelectorAll(ROOT_SELECTOR)).find((node) => states.get(node) === state));
  };

  const firstEditorialMediaId = (productInfo) => {
    const gallery = productInfo.querySelector('media-gallery');
    if (!gallery) return null;

    const variantTargets = new Set(
      Array.from(
        gallery.querySelectorAll('.thumbnail-list_item--variant[data-target]')
      ).map((item) => item.dataset.target)
    );

    const items = Array.from(
      gallery.querySelectorAll('.product__media-item[data-media-id]')
    );

    const editorial =
      items.find((item) => !variantTargets.has(item.dataset.mediaId)) ||
      items[0];

    return editorial?.dataset.mediaId || null;
  };

  const activateMedia = (productInfo, mediaId) => {
    if (!mediaId) return;
    const gallery = productInfo.querySelector('media-gallery');
    if (!gallery) return;
    gallery.setActiveMedia?.(mediaId, false, false, null, { preserveScroll: true });
  };

  const resetToEditorial = (productInfo) => {
    const state = states.get(productInfo);
    if (!state || !hasNeutralRequiredSelection(state)) return;
    activateMedia(productInfo, firstEditorialMediaId(productInfo));
  };

  const scheduleEditorialReset = (productInfo) => {
    const run = () => {
      if (hasNeutralRequiredSelection(states.get(productInfo))) resetToEditorial(productInfo);
    };
    window.requestAnimationFrame(() => window.requestAnimationFrame(run));
    window.setTimeout(run, 140);
  };

  const setNeutral = (
    productInfo,
    { showMessage = false, resetGallery = false } = {}
  ) => {
    const state = states.get(productInfo);
    if (!state) return;

    clearPending(state);
    state.mode = 'neutral';
    state.explicitMediaId = null;
    state.showColorMessage = showMessage;

    removeVariantParam(productInfo);
    reapplySoon(productInfo);

    if (resetGallery) scheduleEditorialReset(productInfo);
  };

  const setExplicit = (productInfo, value = '') => {
    const state = states.get(productInfo);
    if (!state) return;

    state.mode = 'explicit';
    state.showColorMessage = false;
    markPending(state);

    const field = findColorField(productInfo);
    if (field && value) setSelectedText(field, value);

    reapplySoon(productInfo);
  };

  const setModelExplicit = (productInfo, value = '') => {
    const state = states.get(productInfo);
    if (!state) return;

    state.modelMode = 'explicit';
    state.showModelMessage = false;
    markPending(state);

    const field = findModelingField(productInfo);
    if (field) {
      delete field.dataset.evModelNeutral;
      if (value) setSelectedText(field, value);
    }

    reapplySoon(productInfo);
  };


  // EV VARIANT COMPATIBILITY V1 — the option matrix is read from the current
  // Shopify product. Only incomplete combinations are adjusted. This runs
  // during capture, before the theme publishes optionValueSelectionChange.
  const readVariantCombinations = (productInfo, count) => {
    const raw = productInfo.querySelector(
      'variant-selects [data-ev-variant-combinations]'
    )?.textContent;
    if (!raw?.trim()) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed)
        ? parsed.filter((v) => Array.isArray(v.options) && v.options.length === count)
        : [];
    } catch (error) {
      console.warn('[Entrevoltas] Falha ao ler mapa de variantes', error);
      return [];
    }
  };

  const fieldHasValue = (field, value) => {
    const radios = Array.from(field.querySelectorAll('input[type="radio"]'));
    if (radios.length) return radios.some((radio) => radio.value === value);
    const select = field.querySelector('select');
    return Boolean(select && Array.from(select.options).some((item) => item.value === value));
  };

  const setFieldValue = (field, value) => {
    const radio = Array.from(field.querySelectorAll('input[type="radio"]'))
      .find((item) => item.value === value);
    if (radio) {
      radio.checked = true;
      return true;
    }
    const select = field.querySelector('select');
    if (!select) return false;
    const option = Array.from(select.options).find((item) => item.value === value);
    if (!option) return false;
    select.value = value;
    // The original VariantSelects reads the selected attribute, not just
    // the property, when calculating option_value IDs for dropdowns.
    Array.from(select.options).forEach((item) => {
      if (item === option) item.setAttribute('selected', 'selected');
      else item.removeAttribute('selected');
    });
    return true;
  };

  const reconcileInvalidSelection = (productInfo, changedElement) => {
    const state = states.get(productInfo);
    if (!state) return;
    const fields = variantFields(productInfo);
    const changedIndex = fields.findIndex((field) => field.contains(changedElement));
    if (changedIndex < 0 || fields.length < 2) return;

    const variants = readVariantCombinations(productInfo, fields.length);
    if (!variants.length) return;
    const selected = fields.map(currentFieldValue);
    if (selected.some((value) => !value)) return;

    const isExact = (variant) =>
      variant.options.every((value, index) => value === selected[index]);
    // Existing variants, including sold-out variants, MUST remain selected.
    if (variants.some(isExact)) return;

    const modelIndex = fields.indexOf(findModelingField(productInfo));
    const colorIndex = fields.indexOf(findColorField(productInfo));
    const sizeIndex = fields.indexOf(findSizeField(productInfo));

    // EV VARIANT COMPATIBILITY V2 — an already confirmed model must never
    // be substituted to accommodate an unavailable color or size. The order
    // is deliberately model, color, size; any internal fallback becomes
    // NEUTRAL (not a user-confirmed choice) in the loop below.
    let choices = variants;
    const confirmedFields = [];
    if (modelIndex >= 0 && state.modelMode === 'explicit') confirmedFields.push(modelIndex);
    if (colorIndex >= 0 && state.mode === 'explicit') confirmedFields.push(colorIndex);
    if (sizeIndex >= 0 && state.sizeMode === 'explicit') confirmedFields.push(sizeIndex);
    for (const index of confirmedFields) {
      const matching = choices.filter((variant) =>
        variant.options[index] === selected[index]
      );
      if (matching.length) choices = matching;
    }
    // An unconfirmed field that was clicked may be retained when compatible
    // with the confirmed choices, but must NEVER override them.
    if (!confirmedFields.includes(changedIndex)) {
      const matchingClicked = choices.filter((variant) =>
        variant.options[changedIndex] === selected[changedIndex]
      );
      if (matchingClicked.length) choices = matchingClicked;
    }

    // Filter out impossible DOM values, without changing inventory or SKUs.
    choices = choices.filter((variant) =>
      variant.options.every((value, index) => fieldHasValue(fields[index], value))
    );
    if (!choices.length) {
      console.warn('[Entrevoltas] Nenhuma combinação válida para opções selecionadas', selected);
      return;
    }

    const sizeField = sizeIndex >= 0 ? fields[sizeIndex] : null;
    const sizeOptions = sizeField
      ? Array.from(sizeField.querySelectorAll('input[type="radio"]')).map((el) => el.value)
      : [];
    const sizeOrder = sizeOptions.length
      ? sizeOptions
      : sizeField ? Array.from(sizeField.querySelector('select')?.options || []).map((el) => el.value) : [];
    const oldSizePosition = sizeIndex >= 0 ? sizeOrder.indexOf(selected[sizeIndex]) : -1;
    const rank = (variant) => {
      let score = variant.available ? 5 : 0;
      if (modelIndex >= 0 && variant.options[modelIndex] === selected[modelIndex]) score += 10000;
      if (colorIndex >= 0 && variant.options[colorIndex] === selected[colorIndex]) score += 1000;
      if (sizeIndex >= 0 && variant.options[sizeIndex] === selected[sizeIndex]) score += 100;
      if (sizeIndex >= 0 && oldSizePosition >= 0) {
        const newPosition = sizeOrder.indexOf(variant.options[sizeIndex]);
        if (newPosition >= 0) score -= Math.abs(newPosition - oldSizePosition) * 2;
      }
      return score;
    };
    const best = choices.reduce((winner, variant) =>
      !winner || rank(variant) > rank(winner) ? variant : winner, null
    );

    best.options.forEach((value, index) => {
      if (selected[index] === value) return;
      setFieldValue(fields[index], value);
      if (index === modelIndex) {
        state.modelMode = 'neutral';
        state.showModelMessage = true;
      } else if (index === colorIndex) {
        state.mode = 'neutral';
        state.showColorMessage = true;
      } else if (index === sizeIndex) {
        state.sizeMode = 'neutral';
        state.showSizeMessage = true;
        state.sizeUnavailable = true;
      }
    });
    // Never submit the internal fallback as a confirmed customer selection.
    reapplySoon(productInfo);
  };

  const setSizeExplicit = (productInfo, value = '') => {
    const state = states.get(productInfo);
    if (!state) return;
    state.sizeMode = 'explicit';
    state.showSizeMessage = false;
    state.sizeUnavailable = false;
    markPending(state);
    const field = findSizeField(productInfo);
    if (field && value) setSelectedText(field, value);
    reapplySoon(productInfo);
  };

  const finishExplicitWithCurrentVariant = (productInfo, { activate = false } = {}) => {
    const state = states.get(productInfo);
    if (!state) return;

    const variant = readSelectedVariant(productInfo);
    clearPending(state);

    if (selectionComplete(state) && variant?.id) {
      state.explicitMediaId = variantMediaId(productInfo, variant);
      restoreVariantParam(productInfo, variant.id);
      if (activate && state.explicitMediaId) activateMedia(productInfo, state.explicitMediaId);
    } else {
      // A valid internal fallback can display the chosen model/color, but
      // the URL and purchase remain gated until all fields are confirmed.
      const partial = Boolean(variant?.id &&
        (state.mode === 'explicit' || state.modelMode === 'explicit'));
      state.explicitMediaId = partial ? variantMediaId(productInfo, variant) : null;
      removeVariantParam(productInfo);
      if (!partial) scheduleEditorialReset(productInfo);
    }

    reapplySoon(productInfo);
  };

  const evaluateMedia = (productInfo, mediaId) => {
    const state = states.get(productInfo);
    if (
      !state ||
      !selectionComplete(state) ||
      state.variantPending ||
      !mediaId ||
      !state.explicitMediaId
    ) {
      return;
    }

    if (mediaId !== state.explicitMediaId) {
      setNeutral(productInfo);
    }
  };

  const bindGallery = (productInfo) => {
    const state = states.get(productInfo);
    if (!state) return;

    const bind = () => {
      const gallery = productInfo.querySelector('media-gallery');
      const viewer = gallery?.querySelector('[id^="GalleryViewer"]');
      if (!gallery || !viewer) return;

      if (state.boundGallery !== gallery) {
        gallery.addEventListener(
          'click',
          (event) => {
            const target = event.target.closest?.('[data-target]');
            if (!target || !gallery.contains(target)) return;
            evaluateMedia(productInfo, target.dataset.target);
          },
          true
        );
        state.boundGallery = gallery;
      }

      // Programmatic variant scrolling also emits slideChanged.
      // Keep the explicit choice; thumbnail clicks above handle editorial navigation.
    };

    if (customElements.get('media-gallery')) {
      bind();
    } else {
      customElements.whenDefined('media-gallery').then(bind);
    }
  };

  const isMainAddToCartForm = (form, productInfo) =>
    Boolean(
      form &&
      productInfo.contains(form) &&
      form.matches('form[data-type="add-to-cart-form"]')
    );

  const focusableForField = (field) =>
    field?.querySelector('input[type="radio"]:not(:disabled):not(.disabled)') ||
    field?.querySelector('select:not(:disabled)') ||
    null;

  const promptForRequiredSelections = (productInfo) => {
    const state = states.get(productInfo);
    if (!state || !hasNeutralRequiredSelection(state)) return false;

    state.showColorMessage = state.mode === 'neutral';
    state.showModelMessage = state.modelMode === 'neutral';
    state.showSizeMessage = state.sizeMode === 'neutral';
    reapplySoon(productInfo);

    const missing = [];
    const modelField = findModelingField(productInfo);
    const colorField = findColorField(productInfo);

    if (state.modelMode === 'neutral' && modelField) {
      modelField.dataset.evModelAttention = 'true';
      missing.push(modelField);
      window.setTimeout(() => delete modelField.dataset.evModelAttention, 900);
    }

    if (state.mode === 'neutral' && colorField) {
      colorField.dataset.evColorAttention = 'true';
      missing.push(colorField);
      window.setTimeout(() => delete colorField.dataset.evColorAttention, 900);
    }

    const sizeField = findSizeField(productInfo);
    if (state.sizeMode === 'neutral' && sizeField) missing.push(sizeField);
    if (!missing.length) return true;

    const first = missing.sort((a, b) => {
      if (a === b) return 0;
      return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_PRECEDING ? 1 : -1;
    })[0];

    const rect = first.getBoundingClientRect();
    const outside = rect.top < 0 || rect.bottom > window.innerHeight;
    if (outside) first.scrollIntoView({ behavior: 'smooth', block: 'center' });

    focusableForField(first)?.focus?.({ preventScroll: true });
    return true;
  };

  const init = (productInfo) => {
    if (!productInfo || states.has(productInfo)) return;

    const colorField = findColorField(productInfo);
    if (!colorField) return;

    const modelField = findModelingField(productInfo);
    const selectedVariant = readSelectedVariant(productInfo);
    const variantIdFromUrl = new URL(window.location.href).searchParams.get('variant');
    const hasExplicitVariant = Boolean(
      variantIdFromUrl && selectedVariant?.id && String(selectedVariant.id) === String(variantIdFromUrl)
    );

    const state = {
      mode: hasExplicitVariant ? 'explicit' : 'neutral',
      modelMode: modelField ? (hasExplicitVariant ? 'explicit' : 'neutral') : 'explicit',
      sizeMode: hasExplicitVariant || !findSizeField(productInfo) ? 'explicit' : 'neutral',
      showSizeMessage: false,
      explicitMediaId: hasExplicitVariant ? variantMediaId(productInfo, selectedVariant) : null,
      variantPending: false,
      pendingTimer: null,
      showColorMessage: false,
      showModelMessage: false,
      boundGallery: null,
      boundViewer: null
    };

    states.set(productInfo, state);
    productInfo.dataset.evColorContext = 'true';
    ensureMessage(colorField, 'ev-color-required-message', COLOR_REQUIRED_MESSAGE);
    if (modelField) {
      ensureMessage(modelField, 'ev-model-required-message', MODEL_REQUIRED_MESSAGE);
    }
    const sizeField = findSizeField(productInfo);
    if (sizeField) ensureMessage(sizeField, 'ev-size-required-message', SIZE_REQUIRED_MESSAGE);

    if (hasExplicitVariant) {
      reapplySoon(productInfo);
    } else {
      setNeutral(productInfo);
      // Match the rendered default variant without treating it as a user choice.
      const initialMediaId = variantMediaId(productInfo, selectedVariant);
      customElements.whenDefined('media-gallery').then(() => {
        window.requestAnimationFrame(() => {
          if (productInfo.isConnected && !state.variantPending && hasNeutralRequiredSelection(state))
            activateMedia(productInfo, initialMediaId);
        });
      });
    }

    bindGallery(productInfo);
  };

  const initAll = (root = document) => {
    if (root.matches?.(ROOT_SELECTOR)) init(root);
    root.querySelectorAll?.(ROOT_SELECTOR).forEach(init);
  };

  document.addEventListener(
    'click',
    (event) => {
      const stickyButton = event.target.closest?.('.sticky-atc-trigger, .lumin-custom-buy-now, .shopify-payment-button button');
      const productInfo = event.target.closest?.(ROOT_SELECTOR) ||
        (stickyButton ? document.querySelector(ROOT_SELECTOR) : null);
      if (!productInfo) return;

      const state = states.get(productInfo);
      if (!state) return;
      if (stickyButton && !canPurchase(productInfo)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        promptForRequiredSelections(productInfo);
        return;
      }

      const label = event.target.closest?.('label[for]');
      if (!label) return;
      const input = document.getElementById(label.htmlFor);
      if (!input || input.type !== 'radio' || input.disabled) return;
      const fields = variantFields(productInfo);
      if (!fields.some((field) => field.contains(input))) return;
      event.preventDefault();
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    },
    true
  );


  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== ' ' && event.key !== 'Enter') return;
      const input = event.target;
      if (!input?.matches?.('input[type="radio"]:checked')) return;

      const productInfo = input.closest?.(ROOT_SELECTOR);
      if (!productInfo) return;

      const state = states.get(productInfo);
      if (!state) return;

      const colorField = findColorField(productInfo);
      const modelField = findModelingField(productInfo);
      const sizeField = findSizeField(productInfo);

      if (state.mode === 'neutral' && colorField?.contains(input)) {
        setExplicit(productInfo, input.value);
      } else if (state.modelMode === 'neutral' && modelField?.contains(input)) {
        setModelExplicit(productInfo, input.value);
      } else if (state.sizeMode === 'neutral' && sizeField?.contains(input)) {
        setSizeExplicit(productInfo, input.value);
      } else {
        return;
      }

      window.setTimeout(() => {
        finishExplicitWithCurrentVariant(productInfo, { activate: true });
      }, 0);
    },
    true
  );

  document.addEventListener(
    'change',
    (event) => {
      const productInfo = event.target.closest?.(ROOT_SELECTOR);
      if (!productInfo) return;

      const state = states.get(productInfo);
      if (!state) return;

      const variantSelects = event.target.closest?.('variant-selects');
      if (!variantSelects) return;
      if (variantSelects !== productInfo.querySelector('variant-selects')) {
        const oldField = event.target.closest('.product-form__input');
        const activeField = variantFields(productInfo).find((field) =>
          optionNameForField(field) === optionNameForField(oldField)
        );
        const liveInput = activeField && Array.from(activeField.querySelectorAll('input[type="radio"], select'))
          .find((input) => input.tagName === 'SELECT' || input.value === event.target.value);
        event.stopImmediatePropagation();
        if (liveInput) {
          if (liveInput.tagName === 'SELECT') liveInput.value = event.target.value;
          else liveInput.checked = true;
          liveInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }

      const colorField = findColorField(productInfo);
      const modelField = findModelingField(productInfo);
      const sizeField = findSizeField(productInfo);

      if (colorField?.contains(event.target)) {
        setExplicit(productInfo, event.target.value);
      } else if (modelField?.contains(event.target)) {
        setModelExplicit(productInfo, event.target.value);
      } else if (sizeField?.contains(event.target)) {
        setSizeExplicit(productInfo, event.target.value);
      } else {
        markPending(state);
        if (hasNeutralRequiredSelection(state)) reapplySoon(productInfo);
      }
      // Correct incompatible combinations BEFORE global.js reads option IDs.
      reconcileInvalidSelection(productInfo, event.target);
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const productInfo = event.target.closest?.(ROOT_SELECTOR);
      if (!productInfo || !isMainAddToCartForm(event.target, productInfo)) return;

      const state = states.get(productInfo);
      if (canPurchase(productInfo)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      promptForRequiredSelections(productInfo);
    },
    true
  );

  document.addEventListener('ev:product-updated', (event) => {
    const productInfo = event.target.closest?.(ROOT_SELECTOR);
    if (!productInfo) return;

    const state = states.get(productInfo);
    if (!state) return;

    bindGallery(productInfo);
    clearPending(state);

    if (selectionComplete(state) && event.detail?.variant?.id) {
      state.explicitMediaId = variantMediaId(productInfo, event.detail.variant);
      state.showColorMessage = false;
      state.showModelMessage = false;
      restoreVariantParam(productInfo, event.detail.variant.id);
      reapplySoon(productInfo);
    } else {
      const partial = Boolean(event.detail?.variant?.id &&
        (state.mode === 'explicit' || state.modelMode === 'explicit'));
      state.explicitMediaId = partial
        ? variantMediaId(productInfo, event.detail.variant) : null;
      removeVariantParam(productInfo);
      reapplySoon(productInfo);
      if (!partial) scheduleEditorialReset(productInfo);
    }
  });

  document.addEventListener('ev:options-rendered', (event) => {
    const productInfo = event.target.closest?.(ROOT_SELECTOR);
    if (productInfo) applyVisualState(productInfo);
  });

  document.addEventListener('ev:product-update-error', (event) => {
    const productInfo = event.target.closest?.(ROOT_SELECTOR);
    const state = states.get(productInfo);
    if (!state) return;
    clearPending(state);
    state.sizeMode = 'neutral';
    state.showSizeMessage = true;
    state.sizeUnavailable = true;
    reapplySoon(productInfo);
  });

  window.EntrevoltasVariantContext = { sync: applyVisualState, canPurchase };

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), {
      once: true
    });
  } else {
    initAll(document);
  }
})();